import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lock, Sparkles, FileDown, Link2, Landmark, Loader2, ShieldCheck, ScrollText,
  Check, Crown, Plane, Flame, Info, Globe2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { getReport, createCheckout, logEvent } from "@/lib/api";

const cur = (c) => (c === "eur" ? "€" : c === "usd" ? "$" : "");

const KpiCard = ({ n, label, icon: Icon }) => (
  <Card className="p-5 bg-white text-center card-hover">
    <div className="mx-auto h-10 w-10 rounded-xl bg-[hsl(var(--accent))] flex items-center justify-center">
      <Icon className="h-5 w-5 text-[hsl(var(--atlas-navy))]" />
    </div>
    <div className="mt-3 font-display text-4xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">{n}</div>
    <div className="mt-1 text-xs text-muted-foreground">{label}</div>
  </Card>
);

/**
 * TeaserPathwayCard — the free 1-country reveal.
 *
 * Product intent: prove the tool works on THIS visitor's profile without
 * giving away enough to act. So we surface:
 *   • country name
 *   • pathway category + human label (e.g. "Citizenship by descent")
 *   • fit score bar (visual only, no interpretation)
 *
 * We deliberately DO NOT surface:
 *   • the specific legal basis / statute
 *   • the timeline
 *   • the requirements list
 *   • primary sources
 *   • WHY_YOU narrative
 *   • the other N-1 pathways in the report
 *
 * That's what buyers pay for. The card ends with a "N-1 more matches locked
 * below" line so the visitor knows there is significantly more to reveal.
 */
const TeaserPathwayCard = ({ pathway, totalCountries }) => {
  const score = Math.round(Number(pathway.score || 0));
  // Cap the visual bar at 100 (scores can occasionally exceed via weight tuning).
  const barPct = Math.max(0, Math.min(100, score));
  const remainingCountries = Math.max(0, (totalCountries || 0) - 1);
  return (
    <Card
      data-testid="teaser-featured-pathway"
      className="mt-6 p-6 bg-gradient-to-br from-white to-[hsl(var(--atlas-teal))]/5 border-[hsl(var(--atlas-teal))]/30"
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Badge className="bg-[hsl(var(--atlas-teal))] text-white border-0 gap-1">
          <Sparkles className="h-3 w-3" /> Free preview · your top match
        </Badge>
        <span className="text-xs text-muted-foreground">
          A small taste of what your full report contains
        </span>
      </div>
      <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[hsl(var(--atlas-navy))] flex items-center justify-center">
              <Globe2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <div
                className="font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))]"
                data-testid="teaser-featured-country"
              >
                {pathway.country}
              </div>
              <div className="text-sm text-[hsl(var(--atlas-slate))]" data-testid="teaser-featured-category">
                {pathway.category_label}
              </div>
            </div>
          </div>
        </div>
        <div className="md:text-right">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Fit score</div>
          <div
            className="font-display text-3xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums"
            data-testid="teaser-featured-score"
          >
            {score}
            <span className="text-base text-muted-foreground">/100</span>
          </div>
        </div>
      </div>
      <div className="mt-4 h-2 w-full bg-[hsl(var(--muted))] rounded-full overflow-hidden" aria-hidden>
        <div
          className="h-full bg-[hsl(var(--atlas-teal))] transition-[width] duration-500"
          style={{ width: `${barPct}%` }}
        />
      </div>
      {remainingCountries > 0 && (
        <div
          className="mt-4 flex items-center gap-2 text-sm text-[hsl(var(--atlas-slate))]"
          data-testid="teaser-featured-more-locked"
        >
          <Lock className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
          <span>
            <span className="font-semibold text-[hsl(var(--atlas-navy))]">
              +{remainingCountries} more {remainingCountries === 1 ? "country" : "countries"}
            </span>
            {" locked below "}
            <span className="text-muted-foreground">
              — with the specific legal basis, timeline, requirements, and cited sources for each.
            </span>
          </span>
        </div>
      )}
    </Card>
  );
};

const PromoBanner = ({ pricing }) => {
  if (!pricing || !pricing.promo?.active) return null;
  return (
    <div
      data-testid="teaser-promo-banner"
      className="mt-6 flex items-center gap-3 rounded-xl border border-[hsl(var(--atlas-teal))]/40 bg-[hsl(var(--atlas-teal))]/10 px-4 py-3"
    >
      <Flame className="h-4 w-4 text-[hsl(var(--atlas-teal))] shrink-0" />
      <div className="text-sm text-[hsl(var(--atlas-slate))]">
        <span className="font-semibold text-[hsl(var(--atlas-navy))]">
          {pricing.promo.label || "Launch promo"} active.
        </span>{" "}
        Prices below reflect the discount; standard prices are struck through for reference.
      </div>
    </div>
  );
};

const TierCard = ({
  tierKey, block, recommended, currency, promo, selected, onSelect, dataTestId,
}) => {
  const c = cur(currency);
  const promoActive = !!(promo && promo.active && block.pre_promo_price && block.pre_promo_price !== block.price);
  const bullets = tierKey === "citizenship"
    ? [
        "Every citizenship you can claim without relocating",
        "Descent, marriage & registration/restoration routes",
        "Per-country tax overview + primary sources",
        "Downloadable branded PDF report",
      ]
    : [
        "Everything in the Citizenship Report",
        "Residence permits (Plan B bases) & the naturalization they unlock",
        "Investment (CBI) routes shown neutrally, no commissions",
        "Downloadable branded PDF + Tax Explorer deep-dive",
      ];

  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={dataTestId}
      className={`relative text-left rounded-2xl border bg-white p-6 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ${
        selected
          ? "border-[hsl(var(--atlas-navy))] shadow-[0_10px_30px_rgba(15,39,64,0.10)]"
          : "hover:border-[hsl(var(--atlas-steel))]/60"
      }`}
    >
      {recommended && (
        <div className="absolute -top-3 left-6">
          <Badge
            data-testid="teaser-tier-recommended-badge"
            className="bg-[hsl(var(--atlas-teal))] text-white border-0 gap-1"
          >
            <Crown className="h-3 w-3" /> Recommended
          </Badge>
        </div>
      )}
      <div className="flex items-center gap-2">
        {tierKey === "citizenship" ? (
          <ScrollText className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
        ) : (
          <Plane className="h-4 w-4 text-[hsl(var(--atlas-navy))]" />
        )}
        <div className="font-display font-semibold text-lg text-[hsl(var(--atlas-navy))]">
          {block.label}
        </div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{block.blurb}</p>

      <div className="mt-4 flex items-baseline gap-2 flex-wrap" data-testid={`${dataTestId}-price-block`}>
        <span className="font-display text-4xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums">
          {c}{block.price}
        </span>
        {promoActive && (
          <span
            className="text-sm text-muted-foreground line-through tabular-nums"
            data-testid={`${dataTestId}-strike`}
          >
            {c}{block.pre_promo_price}
          </span>
        )}
        <span className="text-sm text-muted-foreground">one-time</span>
        {promoActive && (
          <Badge
            data-testid={`${dataTestId}-promo-badge`}
            className="bg-[hsl(var(--atlas-teal))] text-white border-0 gap-1"
          >
            {promo.label || "Launch promo"}
          </Badge>
        )}
      </div>
      {promoActive && (
        <div className="mt-1 text-[11px] font-medium text-[hsl(var(--atlas-teal))]">
          Promo price applied
        </div>
      )}

      <Separator className="my-4" />
      <ul className="space-y-2 text-sm">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <Check className="h-4 w-4 mt-0.5 text-[hsl(var(--atlas-teal))] shrink-0" />
            <span className="text-[hsl(var(--atlas-slate))]">{b}</span>
          </li>
        ))}
      </ul>

      <div
        className={`mt-5 flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium border ${
          selected
            ? "bg-[hsl(var(--atlas-navy))] text-white border-[hsl(var(--atlas-navy))]"
            : "bg-white text-[hsl(var(--atlas-navy))] border-[hsl(var(--atlas-navy))]/25"
        }`}
      >
        {selected ? (<><Check className="h-4 w-4" /> Selected</>) : "Select this tier"}
      </div>
    </button>
  );
};

export default function Teaser() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const reportId = params.get("report_id");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);
  const [tier, setTier] = useState("full");

  useEffect(() => {
    if (!reportId) { navigate("/check"); return; }
    getReport(reportId).then((d) => {
      setData(d);
      if (d.paid) { navigate(`/report/${reportId}`); return; }
      logEvent("teaser_viewed", reportId, {});
    }).catch(() => toast.error("Could not load your results")).finally(() => setLoading(false));
  }, [reportId, navigate]);

  const pricing = data?.pricing;

  const selectTier = (t) => {
    if (t === tier) return;
    setTier(t);
    logEvent("tier_selected", reportId, { tier: t });
  };

  const checkout = async () => {
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) { toast.error("Please enter a valid email"); return; }
    setCheckingOut(true);
    try {
      const origin = window.location.origin;
      const res = await createCheckout({ report_id: reportId, origin_url: origin, email, tier });
      if (res.url) window.location.href = res.url;
      else throw new Error("no url");
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setCheckingOut(false);
    }
  };

  const selectedPrice = useMemo(() => {
    if (!pricing) return null;
    return pricing[tier]?.price;
  }, [pricing, tier]);

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--atlas-navy))]" /></div>;
  if (!data) return null;
  const t = data.teaser;
  const c = cur(pricing?.currency || "eur");

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <Badge variant="secondary" className="mb-3 gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--atlas-teal))]" /> Your free eligibility summary
        </Badge>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[hsl(var(--atlas-navy))]">
          {data.profile_summary?.name ? `${data.profile_summary.name}, here's what we found` : "Here's what we found"}
        </h1>
        <p className="mt-2 text-muted-foreground">
          Based on your profile, you appear to have pathways across{" "}
          <span className="font-semibold text-[hsl(var(--atlas-navy))]">{t.countries} countries</span>.{" "}
          Unlock the full report to see exactly which ones.
        </p>
      </motion.div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard n={t.descent_claims} label="Citizenship-by-descent claims" icon={ScrollText} />
        <KpiCard n={t.fast_track_naturalization} label="Fast-track naturalization routes" icon={Sparkles} />
        <KpiCard n={t.accessible_residence} label="Accessible residence permits" icon={Landmark} />
        <KpiCard n={t.investment_options} label="Investment (CBI) options" icon={ShieldCheck} />
      </div>

      <PromoBanner pricing={pricing} />

      {/* Tier selection */}
      {pricing && (
        <div className="mt-8 grid md:grid-cols-2 gap-5" data-testid="teaser-tier-cards">
          <TierCard
            tierKey="citizenship"
            block={pricing.citizenship}
            currency={pricing.currency}
            promo={pricing.promo}
            selected={tier === "citizenship"}
            onSelect={() => selectTier("citizenship")}
            dataTestId="teaser-tier-citizenship"
          />
          <TierCard
            tierKey="full"
            block={pricing.full}
            currency={pricing.currency}
            promo={pricing.promo}
            selected={tier === "full"}
            recommended
            onSelect={() => selectTier("full")}
            dataTestId="teaser-tier-full"
          />
        </div>
      )}

      {/* Free 1-country teaser: reveal the top-scoring pathway to prove the tool
          works on THIS user's profile. Country name + category + score only —
          everything actionable (requirements, timeline, source URLs, tax
          overlay) stays behind the paywall. */}
      {data.teaser_pathway && (
        <TeaserPathwayCard pathway={data.teaser_pathway} totalCountries={t.countries} />
      )}

      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        {/* Blurred locked list */}
        <Card className="lg:col-span-3 p-6 bg-white relative overflow-hidden">
          <div className="font-display font-semibold text-lg text-[hsl(var(--atlas-navy))]">Your matched pathways</div>
          <div className="mt-4 space-y-3 select-none" style={{ filter: "blur(6px)", pointerEvents: "none" }} aria-hidden>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="h-3 w-40 bg-[hsl(var(--muted))] rounded" />
                  <div className="h-2 w-24 bg-[hsl(var(--muted))] rounded mt-2" />
                </div>
                <div className="h-6 w-16 bg-[hsl(var(--muted))] rounded" />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center text-center px-6">
              <div className="h-12 w-12 rounded-full bg-[hsl(var(--atlas-navy))] flex items-center justify-center">
                <Lock className="h-5 w-5 text-white" />
              </div>
              <p className="mt-3 text-sm text-[hsl(var(--atlas-slate))] max-w-xs">
                Country names, requirements, timelines, costs, tax summaries and primary sources are unlocked with your report.
              </p>
            </div>
          </div>
        </Card>

        {/* Checkout */}
        <Card className="lg:col-span-2 p-6 bg-white" data-testid="teaser-paywall-card">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">You selected</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-display text-xl font-semibold text-[hsl(var(--atlas-navy))]">
              {pricing?.[tier]?.label}
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span
              className="font-display text-4xl font-semibold text-[hsl(var(--atlas-navy))] tabular-nums"
              data-testid="teaser-selected-price"
            >
              {c}{selectedPrice}
            </span>
            <span className="text-sm text-muted-foreground">one-time · no subscription</span>
          </div>
          <Separator className="my-4" />
          <ul className="space-y-2 text-sm">
            {[
              [Link2, "Every country name + why you matched"],
              [ScrollText, "Requirements, timelines & cost ranges"],
              [ShieldCheck, "Primary-source links + last-verified dates"],
              [Landmark, "Per-country tax summary"],
              [FileDown, "Downloadable branded PDF report"],
            ].map(([Icon, txt]) => (
              <li key={txt} className="flex items-start gap-2">
                <Icon className="h-4 w-4 mt-0.5 text-[hsl(var(--atlas-teal))] shrink-0" /> {txt}
              </li>
            ))}
            {tier === "citizenship" && (
              <li className="mt-2 rounded-lg bg-[hsl(var(--muted))] px-3 py-2 text-[11px] text-[hsl(var(--atlas-slate))]">
                Residence & investment routes are covered in the Full Mobility Report. You can upgrade any time for {c}{pricing?.upgrade?.amount}.
              </li>
            )}
          </ul>
          <div className="mt-5">
            <label className="text-xs text-muted-foreground">Email (for report access)</label>
            <input
              data-testid="teaser-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1 w-full rounded-lg border bg-white/80 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
            />
          </div>
          <Button
            onClick={checkout}
            disabled={checkingOut}
            data-testid="teaser-unlock-report-button"
            className="mt-4 w-full bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white"
          >
            {checkingOut
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting…</>
              : <>Unlock {pricing?.[tier]?.label}</>}
          </Button>
          <p className="mt-2 text-[11px] text-center text-muted-foreground">Secure checkout via Stripe · Test mode</p>
        </Card>
      </div>
    </div>
  );
}
